/** Display-only decoding of BetBurger's fallback labels. Never used for matching. */
type NativeMarket = { period: string; title: string; side: string; line: string; swapped: boolean };

export function nativeMarket(raw: string): NativeMarket | null {
  const bits = raw.split(' · ');
  if (bits.length < 4 || !/^-?\d+(?:\.\d+)?$/.test(bits[3] ?? '')) return null;
  const [code, title, side, line] = bits as [string, string, string, string];
  let period = '';
  const game = code.match(/^(\d+)_set_(\d+)_game$/);
  const part = code.match(/^(\d+)_(set|game|half|quarter|period|map|inning)$/);
  if (game) period = `${game[1]}-й сет, ${game[2]}-й гейм`;
  else if (part) {
    const labels: Record<string, string> = { set: 'й сет', game: 'й гейм', half: 'й тайм', quarter: 'я четверть', period: 'й период', map: 'я карта', inning: 'й иннинг' };
    period = `${part[1]}-${labels[part[2]!]}`;
  } else if (code === 'match') period = 'матч';
  else if (code === 'regular_time') period = 'основное время';
  else if (code === 'with_overtime') period = 'с овертаймом';
  else if (code === 'with_overtime_and_shootouts') period = 'с овертаймом и буллитами';
  else return null;
  return { period, title, side, line, swapped: bits.includes('обратный порядок команд') };
}

// BetBurger packs exact scores as -(first * 1000 + second), e.g. -2001 = 2:1.
// Only decode the verified packed format; unfamiliar encodings must not become a bet.
function exactScore(line: string): string | null {
  const value = Number(line);
  if (!Number.isSafeInteger(value) || value > 0) return null;
  const packed = -value;
  return `${Math.floor(packed / 1000)}:${packed % 1000}`;
}

function titleOf(m: NativeMarket): string {
  if (/^SET_F-SET_F[12]$/.test(m.side)) return `фора по сетам ${m.line}`;
  if (/^SET_OU-SET_T[OU]$/.test(m.side)) return `тотал сетов ${m.line}`;
  if (/^(SET_CS-SET_CS|CS-CS)(_N)?$/.test(m.side)) return m.side.startsWith('SET_') ? 'точный счёт по сетам' : 'точный счёт';
  if (/^DNB-DNB[12]$/.test(m.side)) return 'победа с возвратом при ничьей';
  if (m.side === 'EG-EG' && m.title === 'Exact Total') return `точный тотал ${m.line}`;
  if (/^DC-(1X|X2|12)$/.test(m.side)) return 'двойной шанс';
  if (/^(ML-ML[12]|1X2-[12X])$/.test(m.side)) return 'победитель';
  if (/^OU-T[OU]$/.test(m.side)) return `тотал ${m.line}`;
  if (/^OU[12]-T[OU]$/.test(m.side)) {
    const index = m.side[2] === '1' ? 0 : 1;
    return `инд. тотал участника ${(m.swapped ? 1 - index : index) + 1} ${m.line}`;
  }
  if (/^F-F[12]$/.test(m.side)) return `фора ${m.line}`;
  if (/^BTS-[YN]$/.test(m.side)) return 'обе забьют';
  return /[_#]|^[A-Z\d:-]+$/.test(m.title) ? 'другой рынок' : m.title;
}

export function nativeMarketLabel(raw: string): string | null {
  const markets = raw.split(' / ').map(nativeMarket);
  if (markets.some((m) => m === null)) return null;
  return [...new Set(markets.map((m) => `${titleOf(m!)} · ${m!.period}`))].join(' / ');
}

export function nativeOutcome(raw: string, who: readonly string[]): string | null {
  const m = nativeMarket(raw);
  if (!m) return null;
  const team = (index: number): string => {
    const aligned = m.swapped ? 1 - index : index;
    return who[aligned] || `участник ${aligned + 1}`;
  };
  let pick = 'исход не распознан';
  if (/^SET_F-SET_F[12]$/.test(m.side)) pick = `фора по сетам ${team(m.side.endsWith('1') ? 0 : 1)} ${Number(m.line) > 0 ? '+' : ''}${m.line}`;
  if (/^SET_OU-SET_T[OU]$/.test(m.side)) pick = `тотал сетов ${m.side.endsWith('TO') ? 'больше' : 'меньше'} ${m.line}`;
  if (/^(SET_CS-SET_CS|CS-CS)(_N)?$/.test(m.side)) {
    const score = exactScore(m.line);
    if (score !== null) pick = `точный счёт${m.side.startsWith('SET_') ? ' по сетам' : ''} ${score} (${team(0)} — ${team(1)})${m.side.endsWith('_N') ? ' — нет' : ''}`;
  }
  if (/^DNB-DNB[12]$/.test(m.side)) pick = `победа ${team(m.side.endsWith('1') ? 0 : 1)} — при ничьей возврат`;
  if (m.side === 'EG-EG' && m.title === 'Exact Total') pick = `точный тотал ${m.line}`;
  if (m.side === 'DC-1X') pick = `${team(0)} или ничья`;
  if (m.side === 'DC-X2') pick = `${team(1)} или ничья`;
  if (m.side === 'DC-12') pick = 'победа любой команды — без ничьей';
  if (m.side === 'ML-ML1' || m.side === '1X2-1') pick = `победа ${team(0)}`;
  if (m.side === 'ML-ML2' || m.side === '1X2-2') pick = `победа ${team(1)}`;
  if (m.side === '1X2-X') pick = 'ничья';
  if (/^OU-T[OU]$/.test(m.side)) pick = `тотал ${m.side === 'OU-TO' ? 'больше' : 'меньше'} ${m.line}`;
  if (/^OU[12]-T[OU]$/.test(m.side)) pick = `инд. тотал ${team(m.side[2] === '1' ? 0 : 1)} ${m.side.endsWith('-TO') ? 'больше' : 'меньше'} ${m.line}`;
  if (/^F-F[12]$/.test(m.side)) pick = `фора ${team(m.side === 'F-F1' ? 0 : 1)} ${Number(m.line) > 0 ? '+' : ''}${m.line}`;
  if (/^BTS-[YN]$/.test(m.side)) pick = `обе забьют — ${m.side === 'BTS-Y' ? 'да' : 'нет'}`;
  return `${m.period} — ${pick}`;
}
